import axios from 'axios';
import { getToken, removeToken } from './auth';
import { BACKEND_URL } from './config';

const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE
});

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      removeToken();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const register = (companyName, name, email, password) =>
  axios.post(`${API_BASE}/register`, { companyName, name, email, password });

export const verifyEmail = (token) =>
  axios.post(`${API_BASE}/auth/verify-email`, { token });

export const resendVerification = (email) =>
  axios.post(`${API_BASE}/auth/resend-verification`, { email });

export const login = (email, password) =>
  axios.post(`${API_BASE}/auth/login`, { email, password });

// Sliding session refresh — issues a fresh token for the current user
export const refreshSession = () => api.post('/auth/refresh');
export const applePurchaseSync = () => api.post('/subscription/apple/sync');

// Google (Emergent-managed) login — exchange session_id for an app JWT
export const googleSession = (sessionId) =>
  axios.post(`${API_BASE}/auth/google/session`, { session_id: sessionId });

// Native social login (in-app, iOS/Android) — verify provider token -> app JWT
export const socialGoogle = (idToken) =>
  axios.post(`${API_BASE}/auth/social/google`, { id_token: idToken });

export const socialApple = ({ identityToken, nonce, givenName, familyName }) =>
  axios.post(`${API_BASE}/auth/social/apple`, {
    identity_token: identityToken,
    nonce: nonce || null,
    given_name: givenName || null,
    family_name: familyName || null,
  });

export const changePassword = (currentPassword, newPassword) =>
  api.post('/auth/change-password', { currentPassword, newPassword });

export const forgotPassword = (email) =>
  axios.post(`${API_BASE}/auth/forgot-password`, { email });

export const resetPassword = (token, password) =>
  axios.post(`${API_BASE}/auth/reset-password`, { token, password });

// Team
export const getTeamMembers = () => api.get('/team/members');

export const createInvite = (email, name, role, mobile, propertyIds = []) =>
  api.post('/team/invites', { email, name, role, mobile, propertyIds });

export const getInvites = () => api.get('/team/invites');

export const deleteInvite = (inviteId) => api.delete(`/team/invites/${inviteId}`);

export const acceptInvite = (token, password) =>
  axios.post(`${API_BASE}/team/accept-invite`, { token, password });

export const removeTeamMember = (userId) => api.delete(`/team/members/${userId}`);

export const getStaffProperties = (userId) => api.get(`/team/members/${userId}/properties`);

export const getStaffPerformance = (staffId, start, end) => {
  const params = new URLSearchParams();
  if (start) params.append('start', start);
  if (end) params.append('end', end);
  const qs = params.toString();
  return api.get(`/admin/staff/${staffId}/performance${qs ? `?${qs}` : ''}`);
};

export const getTeamPerformance = (start, end) => {
  const params = new URLSearchParams();
  if (start) params.append('start', start);
  if (end) params.append('end', end);
  const qs = params.toString();
  return api.get(`/admin/team/performance${qs ? `?${qs}` : ''}`);
};

export const getManagerTeamPerformance = (start, end) => {
  const params = new URLSearchParams();
  if (start) params.append('start', start);
  if (end) params.append('end', end);
  const qs = params.toString();
  return api.get(`/cleaning-companies/me/performance${qs ? `?${qs}` : ''}`);
};

export const exportScansCsv = (start, end, staffId) => {
  const params = new URLSearchParams();
  if (start) params.append('start', start);
  if (end) params.append('end', end);
  if (staffId) params.append('staff_id', staffId);
  const qs = params.toString();
  return api.get(`/admin/scans/export${qs ? `?${qs}` : ''}`, { responseType: 'blob' });
};

export const getNotifications = () => api.get('/notifications');

export const markNotificationsSeen = (type) => api.post('/notifications/seen', { type });

export const assignPropertiesToStaff = (userId, propertyIds) => 
  api.post(`/team/members/${userId}/properties`, { propertyIds });

// Properties
export const scanCheckout = (propertySlug, token) =>
  axios.post(`${API_BASE}/scan`, { propertySlug, token, clientTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone });

export const getPublicProperty = (slug, token) =>
  axios.get(`${API_BASE}/property/public/${slug}`, { params: { t: token } });

export const getProperties = () => api.get('/admin/properties');

export const createProperty = (name, address, city, bedrooms, averageCleaningTime, checkoutInstructions) => api.post('/admin/properties', { name, address, city, bedrooms, averageCleaningTime, checkoutInstructions });

export const updateProperty = (id, data) => api.put(`/admin/properties/${id}`, data);

export const deleteProperty = (id) => api.delete(`/admin/properties/${id}`);

export const validatePropertyImport = (file) => {
  const form = new FormData();
  form.append('file', file);
  return api.post('/admin/properties/import/validate', form, { headers: { 'Content-Type': 'multipart/form-data' } });
};

export const commitPropertyImport = (file) => {
  const form = new FormData();
  form.append('file', file);
  return api.post('/admin/properties/import/commit', form, { headers: { 'Content-Type': 'multipart/form-data' } });
};

export const sendSupportChat = (sessionId, message) =>
  api.post('/support/chat', { session_id: sessionId, message });

export const rotateToken = (id) => api.post(`/admin/properties/${id}/rotate-token`);

export const assignProperty = (id, userIds) =>
  api.post(`/admin/properties/${id}/assign`, { userIds });

export const getQRCode = (id) =>
  api.get(`/admin/properties/${id}/qr`, { responseType: 'blob' });

// Scans
export const getScans = (limit = 100, date = null, staffId = null) => {
  const params = new URLSearchParams({ limit: String(limit) });
  if (date) params.append('date', date);
  if (staffId) params.append('staff_id', staffId);
  return api.get(`/admin/scans?${params.toString()}`);
};

// Daily Activity
export const getDailyActivity = (date, staffId = null) => {
  const params = new URLSearchParams();
  if (date) params.append('date', date);
  if (staffId) params.append('staff_id', staffId);
  const qs = params.toString();
  return api.get(`/activity/daily${qs ? `?${qs}` : ''}`);
};

export const startCleaning = (scanId) => api.post(`/scans/${scanId}/start-cleaning`);

export const markScanReady = (scanId) => api.post(`/scans/${scanId}/mark-ready`);

// Manual checkout (staff/admin) when a guest forgets to scan the QR
export const manualCheckout = (propertyId) => api.post(`/properties/${propertyId}/checkout`, { clientTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone });

// Devices (Web Push)
export const registerDevice = (subscription) => api.post('/admin/devices', { subscription });

export const unregisterDevice = (deviceId) => api.delete(`/admin/devices/${deviceId}`);

export const testPush = (message) => api.post('/admin/test-push', { message });

export const getVapidPublicKey = () => axios.get(`${API_BASE}/admin/vapid-public-key`);

// Native Devices (Firebase/APNs)
export const registerNativeDevice = (fcmToken, platform) => 
  api.post('/native/devices', { fcmToken, platform });

export const unregisterNativeDevice = (deviceId) => 
  api.delete(`/native/devices/${deviceId}`);

export const testNativePush = (message) => 
  api.post('/native/test-push', { message });

// Notification Settings
export const getNotificationSettings = () => api.get('/user/notification-settings');

export const updateNotificationSettings = (settings) => api.put('/user/notification-settings', settings);

export const getLateCheckouts = (date = null) => api.get(`/late-checkouts${date ? `?date=${date}` : ''}`);
export const createLateCheckout = (payload) => api.post('/late-checkouts', payload);
export const updateLateCheckout = (id, payload) => api.put(`/late-checkouts/${id}`, payload);
export const deleteLateCheckout = (id) => api.delete(`/late-checkouts/${id}`);

export const getPropertyTasks = (propertyId) => api.get(`/properties/${propertyId}/tasks`);
export const createPropertyTask = (propertyId, payload) => api.post(`/properties/${propertyId}/tasks`, payload);
export const deletePropertyTask = (propertyId, taskId) => api.delete(`/properties/${propertyId}/tasks/${taskId}`);
export const getDueTasks = (date) => api.get(`/tasks/due?date=${date}`);

export const updateUserProfile = (data) => api.put('/user/profile', data);

export const getMe = () => api.get('/user/me');

export const markWelcomeSeen = () => api.post('/user/welcome-seen');
export const markOnboardingSeen = () => api.post('/user/onboarding-seen');

export const uploadProfilePhoto = (file) => {
  const form = new FormData();
  form.append('file', file);
  return api.post('/user/profile-photo', form, { headers: { 'Content-Type': 'multipart/form-data' } });
};

export const deleteProfilePhoto = () => api.delete('/user/profile-photo');

// Permanently delete the current account (Apple/Google store compliance)
export const deleteAccount = () => api.delete('/account', { data: { confirm: 'DELETE' } });

// Maintenance (Professional+ feature)
export const createMaintenance = (formData) =>
  api.post('/maintenance', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const listMaintenance = (params = {}) => api.get('/maintenance', { params });
export const setMaintenanceDone = (id, done, extra = {}) => api.post(`/maintenance/${id}/done`, { done, ...extra });
export const setMaintenanceReminder = (id, reminderAt) => api.post(`/maintenance/${id}/reminder`, { reminderAt });
export const getMaintenanceUrgentCount = () => api.get('/maintenance/urgent-count');
export const exportMaintenance = (params = {}) => api.get('/maintenance/export', { params, responseType: 'blob' });
export const deleteMaintenance = (id) => api.delete(`/maintenance/${id}`);

// Subscription & Billing
export const getSubscription = () => api.get('/subscription');

export const getPricingPlans = (currency) => api.get('/subscription/plans', currency ? { params: { currency } } : {});

export const getBillingHistory = () => api.get('/subscription/billing-history');

export const cancelSubscription = () => api.post('/subscription/cancel');

export const createCheckoutSession = (plan, originUrl, billingCycle = 'month', currency) =>
  api.post('/subscription/create-checkout', { plan, origin_url: originUrl, billing_cycle: billingCycle, currency });

export const changePlan = (plan, billingCycle = 'month') =>
  api.post('/subscription/change-plan', { plan, billing_cycle: billingCycle });

export const detectBillingCurrency = () => api.get('/subscription/detect-currency');

export const createCustomerPortal = () => api.post('/subscription/create-portal');

// Owner / Developer Dashboard (private)
export const getOwnerWhoami = () => api.get('/owner/whoami');
export const getOwnerStats = (params) => api.get('/owner/stats', { params });
export const getOwnerUsers = (params) => api.get('/owner/users', { params });

// Cleaning Companies (admin)
export const getCleaningCompanies = () => api.get('/cleaning-companies');
export const setNextGuests = (scanId, guests) => api.put(`/scans/${scanId}/next-guests`, { guests });
export const createCleaningCompany = (data) => api.post('/cleaning-companies', data);
export const deleteCleaningCompany = (cid) => api.delete(`/cleaning-companies/${cid}`);
export const getCleaningCompanyProperties = (cid) => api.get(`/cleaning-companies/${cid}/properties`);
export const delegateCleaningCompanyProperties = (cid, propertyIds) =>
  api.post(`/cleaning-companies/${cid}/properties`, { propertyIds });

// Cleaning Companies (manager)
export const getMyCleaningCompany = () => api.get('/cleaning-companies/me');
export const managerInviteCleaner = (data) => api.post('/cleaning-companies/me/invites', data);
export const managerDeleteInvite = (inviteId) => api.delete(`/cleaning-companies/me/invites/${inviteId}`);
export const managerRemoveCleaner = (userId) => api.delete(`/cleaning-companies/me/members/${userId}`);
export const managerGetCleanerProperties = (userId) => api.get(`/cleaning-companies/me/members/${userId}/properties`);
export const managerAssignCleanerProperties = (userId, propertyIds) =>
  api.post(`/cleaning-companies/me/members/${userId}/properties`, { propertyIds });

export default api;
